import { Box } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useCallback, useEffect, useState } from 'react';
import CommonInput from './form/common-input.component';
import i18n from '../i18n';
import moment from 'moment';
import PrefixLabeledPillSelect from './prefix-labeled-pill-select.component';
import GlobalNotification from './common/snackbar';
import {
    isCustomDateRangeValid,
    normalizeCustomDateRangeOrder,
} from '../utils/period-date';

/** 周期快捷选项值：本月 / 本周 / 本日 / 自定义日期 */
export type PeriodDateFilterValue =
    | 'this_month'
    | 'this_week'
    | 'today'
    | 'custom';

/**
 * 根据当前日期范围推断周期类型（用于回显）
 * 不匹配预设时返回 custom
 */
export function getPeriodFromDates(
    startDate: string,
    endDate: string
): PeriodDateFilterValue {
    const start = moment(startDate);
    const end = moment(endDate);
    const now = moment();
    if (
        start.isSame(now.clone().startOf('month'), 'day') &&
        end.isSame(now.clone().add(1, 'month').startOf('month'), 'day')
    ) {
        return 'this_month';
    }
    if (
        start.isSame(now.clone().startOf('isoWeek'), 'day') &&
        end.isSame(now.clone().endOf('isoWeek').add(1, 'day'), 'day')
    ) {
        return 'this_week';
    }
    if (
        start.isSame(now.clone().startOf('day'), 'day') &&
        end.isSame(now.clone().add(1, 'day').startOf('day'), 'day')
    ) {
        return 'today';
    }
    return 'custom';
}

/**
 * 根据周期类型计算起止日期（营业日 04:00 分界）
 * 返回 [startDate, endDate]，由调用方写入 store 或 state
 */
export function getDatesForPeriod(
    period: 'this_month' | 'this_week' | 'today'
): [Date, Date] {
    const now = moment();
    let startM: moment.Moment;
    let endM: moment.Moment;

    if (period === 'this_month') {
        startM = now.clone().startOf('month').hour(4).minute(0).second(0);
        endM = now
            .clone()
            .add(1, 'month')
            .startOf('month')
            .hour(4)
            .minute(0)
            .second(0);
    } else if (period === 'this_week') {
        /** ISO 自然周：周一 04:00 至次周一 04:00（覆盖周一至周日） */
        startM = now.clone().startOf('isoWeek').hour(4).minute(0).second(0);
        endM = now
            .clone()
            .endOf('isoWeek')
            .add(1, 'day')
            .hour(4)
            .minute(0)
            .second(0);
    } else {
        startM = now.clone().hour(4).minute(0).second(0);
        if (now.hour() < 4) {
            startM.subtract(1, 'day');
        }
        endM = startM.clone().add(1, 'day');
    }
    return [startM.toDate(), endM.toDate()];
}

/**
 * 自定义日期范围 TextField 外观，与 PrefixLabeledPillSelect（medium）一致：
 * 圆角、填充色、阴影、高度与正文字号/字重
 */
const dateRangeFieldPillMatchSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '16px',
        bgcolor: 'var(--fill-neutral-100)',
        boxShadow: '0px 0px 2px var(--shadow-50)',
        minHeight: 48,
        alignItems: 'center',
        '&:hover': { bgcolor: 'var(--fill-neutral-100)' },
        '&.Mui-focused': { bgcolor: 'var(--fill-neutral-100)' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
        /** 校验失败时保留错误描边，与药丸无错态的「仅阴影」区分 */
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            border: '1px solid',
            borderColor: 'var(--func-error)',
        },
        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid',
            borderColor: 'var(--func-error)',
        },
    },
    '& .MuiOutlinedInput-input': {
        py: 1,
        px: 2,
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '24px',
        color: 'var(--text-dark)',
    },
    '& .MuiFormHelperText-root': { mx: 0 },
} as const;

/** 周期下拉选项（i18n 文案） */
const PERIOD_OPTIONS: { value: PeriodDateFilterValue; label: string }[] = [
    { value: 'this_month', label: 'history_business_page_period_this_month' },
    { value: 'this_week', label: 'history_business_page_period_this_week' },
    { value: 'today', label: 'history_business_page_period_today' },
    { value: 'custom', label: 'history_business_page_period_custom' },
];

export type PeriodDateFilterProps = Readonly<{
    /** 当前开始日期（YYYY-MM-DD HH:mm:ss 等） */
    startDate: string;
    /** 当前结束日期 */
    endDate: string;
    /** 日期时间选择器当前值，选「自定义」时与 startDate/endDate 对应 */
    pickerValue: [Date | null, Date | null];
    /** 日期范围变更回调（预设或自定义选择器变更时调用） */
    onDateTimeChange: (dates: [Date | null, Date | null]) => void;
    /** 是否显示左侧日历图标，默认 true */
    showIcon?: boolean;
    /** 自定义日期时日期范围选择器最小宽度（px），默认 320 */
    dateRangeMinWidth?: number;
}>;

/**
 * 周期 + 自定义日期 筛选组件（本月/本周/本日/自定义日期）
 * 用于数据分析等页右上角统一日期筛选，选「自定义日期」时展示日期时间范围选择器
 */
export default function PeriodDateFilter(props: PeriodDateFilterProps) {
    const {
        startDate,
        endDate,
        pickerValue,
        onDateTimeChange,
        showIcon = true,
        dateRangeMinWidth = 360,
    } = props;

    /** 根据日期推导的周期，用于回显 */
    const currentPeriod = getPeriodFromDates(startDate, endDate);
    /** 用户在下拉中选中的周期，选「自定义」时保持 custom 以展示日期范围选择器 */
    const [selectedPeriod, setSelectedPeriod] = useState<
        PeriodDateFilterValue | null
    >(null);
    /** 下拉展示值：优先用户选择，否则用推导的周期 */
    const displayPeriod = selectedPeriod ?? currentPeriod;
    /** 是否为自定义日期（展示日期范围选择器） */
    const isCustomPeriod = displayPeriod === 'custom';

    /** 日期变化时清空“用户选择”，回显由日期推导的周期 */
    useEffect(() => {
        setSelectedPeriod(null);
    }, [startDate, endDate]);

    /** 周期选项（label 做 i18n 翻译） */
    const periodItems = PERIOD_OPTIONS.map((opt) => ({
        value: opt.value,
        label: i18n.t(opt.label),
    }));

    /** 切换周期：选预设则计算日期并回调；选自定义仅展示选择器 */
    const handlePeriodChange = useCallback(
        (value: string) => {
            setSelectedPeriod(value as PeriodDateFilterValue);
            if (value === 'custom') return;
            const [start, end] = getDatesForPeriod(
                value as 'this_month' | 'this_week' | 'today'
            );
            onDateTimeChange([start, end]);
        },
        [onDateTimeChange]
    );

    /**
     * 自定义范围变更：选完起止后再校验并提示，与 App PeriodDateFilter 一致（日历不禁用、不自动 clamp）。
     * @param dates - 选择器回传的起止日（可含 null，表示尚未选完）
     */
    const handleCustomRangeChange = useCallback(
        (dates: [Date | null, Date | null] | undefined) => {
            if (!dates) return;
            const [rawStart, rawEnd] = dates;
            if (!rawStart || !rawEnd) {
                onDateTimeChange(dates);
                return;
            }

            const [start, end] = normalizeCustomDateRangeOrder(rawStart, rawEnd);
            if (!isCustomDateRangeValid(start, end)) {
                GlobalNotification.error(
                    i18n.t('business_analysis_page_the_date_interval_cannot_exceed_31')
                );
                return;
            }
            onDateTimeChange([start, end]);
        },
        [onDateTimeChange]
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <PrefixLabeledPillSelect
                options={periodItems.map((opt) => ({
                    value: opt.value ?? '',
                    label: opt.label ?? '',
                }))}
                value={displayPeriod}
                onChange={(value) =>
                    handlePeriodChange(value as PeriodDateFilterValue)
                }
                leadingIcon={showIcon ? <CalendarMonthIcon sx={{ width: 32, height: 32 }} /> : undefined}
            />
            {isCustomPeriod && (
                <Box
                    sx={{ minWidth: dateRangeMinWidth, ...dateRangeFieldPillMatchSx }}
                    className="data-analysis-date-picker"
                >
                    <CommonInput
                        inputType="DATE_TIME_RANGE"
                        rangeHandleChangePicker={handleCustomRangeChange}
                        rangeValue={pickerValue}
                    />
                </Box>
            )}
        </Box>
    );
}
